// src/pages/LandingPage.tsx
// Landing page dengan hero section dan navigasi

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Trophy, BookOpen, Star, Youtube, Instagram } from "lucide-react";
import { useProfileData } from "../hooks/useProfileData";
import { useSocialLinks } from "../hooks/useSocialLinks";
import Navbar from "../components/Navbar";
import TiltedCard from "../components/TiltedCard/TiltedCard";
import SpotlightCard from "../components/SpotlightCard/SpotlightCard";
import AnimatedContent from "../Animations/AnimatedContent/AnimatedContent";
import FadeContent from "../Animations/FadeContent/FadeContent";
import BlurText from "../TextAnimations/BlurText/BlurText";
import AchievementModal from "../components/profile/AchievementModal";
import { PhotoProvider } from 'react-photo-view';
import LikeButton from "../components/common/LikeButton";

const TikTokIcon: React.FC<{ className?: string }> = ({ className = "h-4 w-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.298-.002.595.042.88.13V9.4a6.33 6.33 0 0 0-1-.08A6.34 6.34 0 0 0 3 15.66a6.34 6.34 0 0 0 10.82 4.47V10.89a8.27 8.27 0 0 0 5.77 2.31v-3.45a4.85 4.85 0 0 1-3.77-3.06z" />
  </svg>
);

const LandingPage: React.FC = () => {
    const { data, loading, error } = useProfileData();
    const { socialLinks } = useSocialLinks();
    const [selectedAchievement, setSelectedAchievement] = useState<any>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleAchievementClick = (achievement: any) => {
        setSelectedAchievement(achievement);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedAchievement(null);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Memuat data profil...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="text-red-500 mb-4">
                        <svg
                            className="w-12 h-12 mx-auto"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                    </div>
                    <p className="text-gray-600 mb-4">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Coba Lagi
                    </button>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
                <p className="text-gray-600">Data tidak tersedia</p>
            </div>
        );
    }

    return (
        <PhotoProvider>
            <div className="min-h-screen bg-gray-50">
            {/* Navigation */}
            <Navbar />

            {/* Hero Section */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="lg:grid lg:grid-cols-2 lg:gap-8 items-center">
                    {/* Content */}
                    <div className="mb-12 lg:mb-0">
                        <div className="text-center lg:text-left">
                            <AnimatedContent
                                {...({
                                    direction: "vertical",
                                    distance: 50,
                                    duration: 1,
                                } as any)}
                            >
                                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                                    <BlurText
                                        text="Halo, Saya"
                                        className="inline-block mr-4 text-2xl md:text-3xl lg:text-4xl"
                                        animateBy="words"
                                        direction="top"
                                        delay={10}
                                        stepDuration={0.6}
                                        onAnimationComplete={() => {}}
                                    />
                                    <BlurText
                                        text={data.fullName}
                                        className="text-blue-600 inline-block whitespace-nowrap"
                                        style={{
                                            fontFamily: "Bungee, sans-serif",
                                        }}
                                        animateBy="words"
                                        direction="bottom"
                                        delay={10}
                                        stepDuration={0.8}
                                        onAnimationComplete={() => {}}
                                    />
                                </h1>
                            </AnimatedContent>
                            <FadeContent delay={0.5}>
                                <BlurText
                                    text={data.bio}
                                    className="text-xl text-gray-700 mb-8 max-w-2xl"
                                    animateBy="words"
                                    direction="top"
                                    delay={10}
                                    stepDuration={0.4}
                                    onAnimationComplete={() => {}}
                                />
                            </FadeContent>

                            {/* Stats */}
                            <AnimatedContent
                                {...({
                                    direction: "vertical",
                                    distance: 30,
                                    delay: 0.8,
                                } as any)}
                            >
                                <div className="grid grid-cols-3 gap-4 mb-8">
                                    <FadeContent delay={1}>
                                        <div className="text-center">
                                            <Link to="/profile#achievements">
                                                <SpotlightCard 
                                                    className="bg-white rounded-lg p-4 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 cursor-pointer"
                                                    spotlightColor="rgba(255, 193, 7, 0.25)"
                                                >
                                                    <Trophy className="h-8 w-8 text-yellow-400 mx-auto mb-2" />
                                                    <div className="text-2xl font-bold text-gray-900">
                                                        {data.achievements.length}
                                                    </div>
                                                    <div className="text-sm text-gray-600">
                                                        Prestasi
                                                    </div>
                                                </SpotlightCard>
                                            </Link>
                                        </div>
                                    </FadeContent>
                                    <FadeContent delay={1.2}>
                                        <div className="text-center">
                                            <Link to="/profile#education">
                                                <SpotlightCard 
                                                    className="bg-white rounded-lg p-4 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 cursor-pointer"
                                                    spotlightColor="rgba(34, 197, 94, 0.25)"
                                                >
                                                    <BookOpen className="h-8 w-8 text-green-400 mx-auto mb-2" />
                                                    <div className="text-2xl font-bold text-gray-900">
                                                        {
                                                            data.educationHistory
                                                                .length
                                                        }
                                                    </div>
                                                    <div className="text-sm text-gray-600">
                                                        Pendidikan
                                                    </div>
                                                </SpotlightCard>
                                            </Link>
                                        </div>
                                    </FadeContent>
                                    <FadeContent delay={1.4}>
                                        <div className="text-center">
                                            <Link to="/profile#courses">
                                                <SpotlightCard 
                                                    className="bg-white rounded-lg p-4 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 cursor-pointer"
                                                    spotlightColor="rgba(168, 85, 247, 0.25)"
                                                >
                                                    <Star className="h-8 w-8 text-purple-400 mx-auto mb-2" />
                                                    <div className="text-2xl font-bold text-gray-900">
                                                        {data.courseHistory?.length || 0}
                                                    </div>
                                                    <div className="text-sm text-gray-600">
                                                        Kursus
                                                    </div>
                                                </SpotlightCard>
                                            </Link>
                                        </div>
                                    </FadeContent>
                                </div>
                            </AnimatedContent>

                            {/* CTA Button & Social Media */}
                            <FadeContent delay={1.6}>
                                <div className="text-center lg:text-left space-y-4">
                                    <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 sm:gap-4">
                                        <Link
                                            to="/profile"
                                            className="inline-flex items-center bg-blue-500 text-white px-7 py-3.5 rounded-2xl text-base font-semibold hover:bg-blue-600 transition-colors shadow-lg hover:shadow-xl"
                                        >
                                            Lihat Profil Lengkap
                                            <ArrowRight className="ml-2 h-5 w-5" />
                                        </Link>
                                        <LikeButton />
                                    </div>

                                    {/* Social Media Links */}
                                    <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 pt-2">
                                        {socialLinks.youtube && (
                                            <a
                                                href={socialLinks.youtube}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium text-sm shadow hover:shadow-md transition-all transform hover:-translate-y-0.5"
                                                title="YouTube Vano"
                                            >
                                                <Youtube className="w-4 h-4" />
                                                <span>YouTube</span>
                                            </a>
                                        )}
                                        {socialLinks.tiktok && (
                                            <a
                                                href={socialLinks.tiktok}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 hover:bg-black text-white font-medium text-sm shadow hover:shadow-md transition-all transform hover:-translate-y-0.5"
                                                title="TikTok Vano"
                                            >
                                                <TikTokIcon className="w-4 h-4" />
                                                <span>TikTok</span>
                                            </a>
                                        )}
                                        {socialLinks.instagram && (
                                            <a
                                                href={socialLinks.instagram}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 via-pink-600 to-amber-500 hover:opacity-95 text-white font-medium text-sm shadow hover:shadow-md transition-all transform hover:-translate-y-0.5"
                                                title="Instagram Vano"
                                            >
                                                <Instagram className="w-4 h-4" />
                                                <span>Instagram</span>
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </FadeContent>
                        </div>
                    </div>

                    <div className="relative h-96 lg:h-[500px] flex items-center justify-center">
                        <div className="relative">
                            <TiltedCard
                                imageSrc={
                                    data.profileImageUrl ||
                                    "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iIzNCODJGNiIvPjx0ZXh0IHg9IjIwMCIgeT0iMTUwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSIgZmlsbD0id2hpdGUiIGZvbnQtc2l6ZT0iMjQiIGZvbnQtZmFtaWx5PSJBcmlhbCI+UHJvZmlsZTwvdGV4dD48L3N2Zz4="
                                }
                                altText={`${data.fullName} - Profil`}
                                containerHeight="384px"
                                containerWidth="320px"
                                imageHeight="384px"
                                imageWidth="320px"
                                captionText={data.fullName}
                                showMobileWarning={false}
                            />
                            {/* Decorative elements */}
                            <div className="absolute -top-4 -right-4 w-24 h-24 bg-yellow-400 rounded-full opacity-20 animate-pulse"></div>
                            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-blue-400 rounded-full opacity-20 animate-pulse delay-1000"></div>
                        </div>
                    </div>
                </div>

                {/* Quick Highlights */}
                <div className="mt-20">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
                            Pencapaian Terbaru
                        </h2>
                        <p className="text-gray-600 max-w-2xl mx-auto">
                            Berikut adalah beberapa pencapaian terbaru yang telah diraih
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 justify-items-center">
                        {data.achievements
                            .slice(0, 4)
                            .map((achievement, index) => (
                                <FadeContent
                                    key={achievement.id}
                                    delay={0.8 + index * 0.2}
                                >
                                    <div onClick={() => handleAchievementClick(achievement)} className="cursor-pointer">
                                        <TiltedCard
                                            imageSrc={
                                                achievement.image_url?.trim() ||
                                                "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjgwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjgwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI0Y5RkFGQiIvPjx0ZXh0IHg9IjE0MCIgeT0iMTAwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSIgZmlsbD0iIzZCNzI4MCIgZm9udC1zaXplPSIxNiIgZm9udC1mYW1pbHk9IkFyaWFsIj5QcmVzdGFzaTwvdGV4dD48L3N2Zz4="
                                            }
                                            altText={achievement.title}
                                            containerHeight="200px"
                                            containerWidth="280px"
                                            imageHeight="200px"
                                            imageWidth="280px"
                                            showMobileWarning={false}
                                            displayOverlayContent={true}
                                            overlayContent={
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex flex-col justify-end p-4">
                                                    <div className="text-center text-white">
                                                        <Trophy className="h-6 w-6 text-yellow-400 mx-auto mb-2" />
                                                        <h3 className="font-semibold text-white mb-2 text-sm">
                                                            {achievement.title}
                                                        </h3>
                                                        <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full border border-white/30">
                                                            {achievement.level} •{" "}
                                                            {new Date(
                                                                achievement.achievement_date
                                                            ).getFullYear()}
                                                        </span>
                                                    </div>
                                                </div>
                                            }
                                        />
                                    </div>
                                </FadeContent>
                            ))}
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="bg-white border-t border-gray-200 mt-20 pb-20 sm:pb-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="text-center text-gray-600">
                        <p>
                            &copy; {new Date().getFullYear()} {data.fullName}.
                            Dibuat dengan ❤️
                        </p>
                    </div>
                </div>
            </footer>

            {/* Achievement Modal */}
            <AchievementModal
                achievement={selectedAchievement}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
            />
            </div>
        </PhotoProvider>
    );
};

export default LandingPage;
